<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { stages, stageLabel } from './stores';
  import {
    getMetaHealth, listMetaTemplates, upsertMetaTemplate,
    commitMetaJob, getMetaJob, listMetaJobs, cancelMetaJob,
    getContacts,
    type MetaSenderHealth, type MetaTemplate, type MetaJob, type MetaTarget,
    type Contact,
  } from './api';

  // ── Health check ───────────────────────────────────────────────────────
  let health = $state<MetaSenderHealth[]>([]);
  let healthReady = $state(false);
  let healthLoading = $state(true);

  async function loadHealth() {
    healthLoading = true;
    try {
      const r = await getMetaHealth();
      health = r.senders;
      healthReady = r.ready;
    } catch (e: any) {
      toast = e?.message || 'Health check failed';
    }
    healthLoading = false;
  }

  // ── Templates ──────────────────────────────────────────────────────────
  let templates = $state<MetaTemplate[]>([]);
  let selectedTemplateId = $state('agnico');
  let editingTemplate = $state<MetaTemplate | null>(null);
  let editLabel = $state('');
  let editSubject = $state('');
  let editBody = $state('');

  let selectedTemplate = $derived(
    templates.find(t => t.id === selectedTemplateId)
  );

  async function loadTemplates() {
    try {
      const r = await listMetaTemplates();
      templates = r.templates;
      if (templates.length && !templates.find(t => t.id === selectedTemplateId)) {
        const def = templates.find(t => t.is_default) || templates[0];
        selectedTemplateId = def.id;
      }
    } catch {}
  }

  function startEdit(t: MetaTemplate) {
    editingTemplate = t;
    editLabel = t.label;
    editSubject = t.subject;
    editBody = t.body;
  }
  function cancelEdit() { editingTemplate = null; }

  async function saveEdit() {
    if (!editingTemplate) return;
    try {
      await upsertMetaTemplate({
        id: editingTemplate.id, label: editLabel, subject: editSubject,
        body: editBody, is_default: !!editingTemplate.is_default,
      });
      await loadTemplates();
      editingTemplate = null;
      toast = 'Template saved';
    } catch (e: any) { toast = e?.message || 'Save failed'; }
    setTimeout(() => toast = '', 3000);
  }

  // ── Strafe-style contact selection ─────────────────────────────────────
  type EngagementBucket = 'never_contacted' | 'engaged' | 'stale';
  const ENGAGEMENT_LABELS: Record<EngagementBucket, string> = {
    never_contacted: 'New',
    engaged: 'Recent',
    stale: 'Stale',
  };

  let contactsLoaded = $state(false);
  let allContacts = $state<Contact[]>([]);
  let filterTags = $state<Set<string>>(new Set());
  let filterStages = $state<Set<string>>(new Set());
  let filterEngagement = $state<Set<EngagementBucket>>(new Set());
  let uncheckedEmails = $state<Set<string>>(new Set());

  function classifyEngagement(c: Contact): EngagementBucket {
    if (!c.thread_count) return 'never_contacted';
    if (!c.last_seen) return 'stale';
    const ls = new Date(c.last_seen);
    if (isNaN(ls.getTime())) return 'stale';
    const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
    return ls.getTime() >= cutoff ? 'engaged' : 'stale';
  }

  let allTags = $derived.by(() => {
    const s = new Set<string>();
    for (const c of allContacts) s.add(c.tag || '');
    return Array.from(s).sort((a, b) => (a === '' ? 1 : b === '' ? -1 : a.localeCompare(b)));
  });

  let allStages = $derived.by(() => {
    const s = new Set<string>();
    for (const c of allContacts) {
      const stage = $stages[c.client] || '';
      if (stage && stage !== 'Non-Clients') s.add(stage);
    }
    return Array.from(s).sort();
  });

  let filteredContacts = $derived.by(() => {
    return allContacts.filter(c => {
      if (($stages[c.client] || '') === 'Non-Clients') return false;
      if (filterTags.size && !filterTags.has(c.tag || '')) return false;
      if (filterStages.size) {
        const stage = $stages[c.client] || '';
        if (!filterStages.has(stage)) return false;
      }
      if (filterEngagement.size && !filterEngagement.has(classifyEngagement(c))) return false;
      return true;
    });
  });

  let selectedContacts = $derived.by(() =>
    filteredContacts.filter(c => !uncheckedEmails.has(c.email))
  );

  function toggleTag(t: string) {
    const next = new Set(filterTags);
    next.has(t) ? next.delete(t) : next.add(t);
    filterTags = next;
  }
  function toggleStage(s: string) {
    const next = new Set(filterStages);
    next.has(s) ? next.delete(s) : next.add(s);
    filterStages = next;
  }
  function toggleEngagement(b: EngagementBucket) {
    const next = new Set(filterEngagement);
    next.has(b) ? next.delete(b) : next.add(b);
    filterEngagement = next;
  }
  function toggleContact(email: string) {
    const next = new Set(uncheckedEmails);
    next.has(email) ? next.delete(email) : next.add(email);
    uncheckedEmails = next;
  }
  function checkAll() { uncheckedEmails = new Set(); }
  function uncheckAll() { uncheckedEmails = new Set(filteredContacts.map(c => c.email)); }
  function invertSelection() {
    const next = new Set<string>();
    for (const c of filteredContacts) {
      if (!uncheckedEmails.has(c.email)) next.add(c.email);
    }
    uncheckedEmails = next;
  }

  // ── Cold-list paste (for recipients NOT in CRM) ────────────────────────
  let coldListRaw = $state('');
  let coldListContacts = $derived.by(() => {
    const out: { email: string; company: string; first_name: string }[] = [];
    for (const line of coldListRaw.split('\n')) {
      const parts = line.split(',').map(p => p.trim());
      const email = parts[0];
      if (!email || !email.includes('@')) continue;
      out.push({
        email,
        company: parts[1] || '',
        first_name: parts[2] || (email.split('@')[0].split('.')[0].replace(/^./, c => c.toUpperCase())),
      });
    }
    return out;
  });

  // Final combined recipient list (CRM-selected + cold-list paste)
  let combinedRecipients = $derived.by(() => {
    const seen = new Set<string>();
    const out: { email: string; company: string; first_name: string }[] = [];
    for (const c of selectedContacts) {
      const key = c.email.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      const firstName = (c.name || '').split(' ')[0] || c.email.split('@')[0].split('.')[0].replace(/^./, ch => ch.toUpperCase());
      out.push({ email: c.email, company: c.client || '', first_name: firstName });
    }
    for (const r of coldListContacts) {
      const key = r.email.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(r);
    }
    return out;
  });

  // ── Schedule ───────────────────────────────────────────────────────────
  let delay2 = $state(3);
  let delay3 = $state(7);
  let initialOffset = $state(0);
  // Per-recipient send_at stagger (seconds). With 50 targets and stagger=60,
  // recipient N's email-1 fires at NOW + N*60s. Smooths the burst pattern so
  // all 150 emails (50×3) don't hit Gmail's outbound queue in 5-10 minutes.
  // 0 = no stagger (all enqueued send_ats coincide; scheduler dispatches them
  // back-to-back in the same tick).
  let dispatchStagger = $state(60);

  // ── Email-1 sender (Marcus envelope) ───────────────────────────────────
  // Both addresses send AS the "Marcus Dealton" persona — only the envelope
  // differs. partner@ is the existing default; marcus@ matches the persona name
  // and may land differently in inboxes.
  let email1Sender = $state<'partner@dealton.ai' | 'marcus@dealton.ai'>('marcus@dealton.ai');

  // ── Auto-cancel on signup ──────────────────────────────────────────────
  // If a recipient becomes a Dealton user mid-campaign, runner skips their
  // remaining emails. Default ON — the alternative (someone signs up but keeps
  // getting AI-persona cold mail) is awkward.
  let cancelOnSignup = $state(true);

  // ── Email preview ──────────────────────────────────────────────────────
  let showPreview = $state(true);

  // Render strings (mirrors what the runner does for first_name/backstory placeholders)
  let previewFirstName = $derived(
    combinedRecipients[0]?.first_name || 'Sample'
  );
  let previewCompany = $derived(
    combinedRecipients[0]?.company || 'Acme Corp'
  );

  let previewEmail1 = $derived(`Hi ${previewFirstName},

I'm Marcus Dealton, the AI partner Dealton built when Daniel (fellow YC founder, founder of Dealton) asked us to show you what your version would look like.

[Marcus's backstory — auto-generated per recipient based on their company. The dark-mode template produces ~200 words about a deceased mining/sales expert whose knowledge lives on in the AI. Tailored expert_type and industry derived from researching ${previewCompany}.]

In practice that means: I track every deal in your pipeline (no manual CRM entry), write your prospecting and follow-up emails grounded in real client history, and join your sales calls as a domain expert with full context. I exist so you can focus on closing.

Sign up at app.dealton.ai and your version of me is yours. Sara, Daniel's actual AI partner, will send you a real example next week.

[Marcus's signature block]`);

  let previewEmail2 = $derived(`Hi ${previewFirstName},

I'm Sara, the AI partner Dealton built for Daniel's company. He asked me to send you something honest.

Cold-prospect briefings aren't where AI partners shine. The wedge is what comes next: making sense of a relationship in motion. Instead of pretending I have deep insight into a target you've never emailed, here's a real briefing I sent Daniel last week, a deal he's working at Geomet (his company; Dealton started as an internal tool we're now sharing with the world).

This is the kind of read Marcus would write you on day 10.

> ${(selectedTemplate?.body || '').split('\n').join('\n> ')}

This is the kind of thinking Marcus would do for you, every day. Sign in: app.dealton.ai

[Sara's signature block]`);

  let previewEmail3 = `Hi [recipient],

Hope you liked what Marcus and Sara prepared for you. Some context: I built StratumAI (mining tech) for five years before this into a ~$100M business. Started GeoMet AI last year in the mining industry, and built the entire platform end-to-end with Claude Code. Felt like one of those hackers in the movies. Engineering had crossed the singularity. I built more IP and product with a team of 2 in 2 months than a team of 10 had in 5 years.

Then I switched gears to sales. And it was the opposite of productive. Existing CRMs were a time sink, emails written from scratch, deals slipping because nothing connected. The AI products on the market were shoddy, and dumping email threads into Claude one at a time barely saved me any time. The AI revolution that happened in engineering hadn't really touched sales.

So I built Dealton. Basically Claude Code for sales: tracks every deal, writes every email, co-leads every call. The point isn't to replace your team. It's to turn each of you into a superweapon, with an AI partner that's actually got a soul and works as a true team member. The secret is a knowledge graph per client that Claude traverses, pulled from your calls, emails, product collateral, and the public web.

[Daniel's signature block]`;

  // ── Launch / active job ────────────────────────────────────────────────
  let launching = $state(false);
  let activeJobId = $state<string | null>(null);
  let activeJob = $state<MetaJob | null>(null);
  let activeTargets = $state<MetaTarget[]>([]);
  let pollHandle: ReturnType<typeof setInterval> | null = null;
  let toast = $state('');

  async function launch() {
    if (!healthReady) {
      toast = 'All three sender accounts must be healthy.';
      setTimeout(() => toast = '', 4000); return;
    }
    if (combinedRecipients.length === 0) {
      toast = 'Pick at least one recipient (filter contacts or paste a cold list).';
      setTimeout(() => toast = '', 4000); return;
    }
    if (!confirm(`Launch Meta campaign to ${combinedRecipients.length} recipient(s)?`)) return;
    launching = true;
    try {
      const r = await commitMetaJob({
        template_id: selectedTemplateId,
        recipients: combinedRecipients,
        initial_offset_minutes: initialOffset,
        delay_2_days: delay2, delay_3_days: delay3,
        email_1_sender: email1Sender,
        cancel_on_signup: cancelOnSignup,
        dispatch_stagger_seconds: dispatchStagger,
      });
      activeJobId = r.job_id;
      toast = `Launched job ${r.job_id} (${r.total} targets)`;
      startPolling();
    } catch (e: any) {
      const msg = e?.message || 'Launch failed';
      toast = msg.length > 200 ? msg.slice(0, 200) : msg;
    }
    launching = false;
    setTimeout(() => toast = '', 5000);
  }

  function startPolling() { stopPolling(); pollJob(); pollHandle = setInterval(pollJob, 5000); }
  function stopPolling() { if (pollHandle) { clearInterval(pollHandle); pollHandle = null; } }

  async function pollJob() {
    if (!activeJobId) return;
    try {
      const r = await getMetaJob(activeJobId);
      activeJob = r.job;
      activeTargets = r.targets;
      if (['completed', 'cancelled', 'failed'].includes(r.job.status)) {
        stopPolling();
        await loadJobHistory();
      }
    } catch {}
  }

  async function cancelActive() {
    if (!activeJobId) return;
    if (!confirm('Cancel this Meta campaign?')) return;
    try {
      await cancelMetaJob(activeJobId);
      stopPolling();
      activeJobId = null; activeJob = null; activeTargets = [];
      toast = 'Cancelled';
      await loadJobHistory();
    } catch (e: any) { toast = e?.message || 'Cancel failed'; }
    setTimeout(() => toast = '', 3000);
  }

  // ── Job history ────────────────────────────────────────────────────────
  let jobHistory = $state<MetaJob[]>([]);
  async function loadJobHistory() { try { jobHistory = await listMetaJobs(); } catch {} }

  function viewJob(jobId: string) { activeJobId = jobId; pollJob(); startPolling(); }
  function formatDate(s?: string): string {
    if (!s) return '';
    try { return new Date(s.replace(' ', 'T')).toLocaleString(); } catch { return s; }
  }

  // ── Lifecycle ──────────────────────────────────────────────────────────
  onMount(async () => {
    await Promise.all([
      loadHealth(),
      loadTemplates(),
      loadJobHistory(),
      (async () => {
        try { allContacts = await getContacts(); } catch {}
        contactsLoaded = true;
      })(),
    ]);
    const live = jobHistory.find(j => j.status === 'running' || j.status === 'queued');
    if (live) { activeJobId = live.id; pollJob(); startPolling(); }
  });
  onDestroy(() => stopPolling());
</script>

<div class="meta-tab">
  <div class="meta-header">
    <h3>Meta — YC Outreach Campaign</h3>
    <p class="text-dim" style="font-size:12px;">Three-email sequence (Marcus → Sara → Daniel) sent through partner@, sara@, daniel@dealton.ai. Dealton-tenant only.</p>
  </div>

  {#if toast}
    <div class="meta-toast">{toast}</div>
  {/if}

  <!-- ── Health ── -->
  <div class="card">
    <div class="card-header">
      <h4>Sender health</h4>
      <button class="btn-small" onclick={loadHealth} disabled={healthLoading}>{healthLoading ? '…' : 'Re-check'}</button>
    </div>
    <div class="sender-grid">
      {#each health as s}
        <div class="sender-row">
          <span class="status-dot status-{s.status}"></span>
          <span class="sender-email">{s.email}</span>
          <span class="sender-detail text-dim">{s.detail}</span>
        </div>
      {/each}
    </div>
  </div>

  <!-- ── Template picker ── -->
  <div class="card">
    <div class="card-header"><h4>Email 2 template</h4></div>
    <div class="template-list">
      {#each templates as t}
        <label class="template-card" class:selected={selectedTemplateId === t.id}>
          <input type="radio" bind:group={selectedTemplateId} value={t.id} />
          <div class="template-card-body">
            <div class="template-card-header">
              <span class="template-label">{t.label}</span>
              <button class="btn-tiny" onclick={(e) => { e.preventDefault(); startEdit(t); }}>Edit</button>
            </div>
            <div class="template-subject">Subject: <em>{t.subject}</em></div>
            <div class="template-preview">{t.body.slice(0, 200)}…</div>
          </div>
        </label>
      {/each}
    </div>
  </div>

  <!-- ── Filters (Strafe-style) ── -->
  <div class="card">
    <div class="card-header"><h4>Filters</h4></div>
    <div class="text-xs text-dim" style="margin-bottom:8px;">AND across rows · OR within a row · Non-Clients excluded automatically</div>

    <div class="strafe-filter-row">
      <div class="strafe-filter-label">Tag</div>
      <div class="chip-row">
        {#each allTags as t}
          <button class="chip" class:selected={filterTags.has(t)} onclick={() => toggleTag(t)}>
            {t === '' ? 'Untagged' : t}
          </button>
        {/each}
        {#if allTags.length === 0}<span class="text-xs text-dim">No tags yet.</span>{/if}
      </div>
    </div>

    <div class="strafe-filter-row">
      <div class="strafe-filter-label">Company Stage</div>
      <div class="chip-row">
        {#each allStages as s}
          <button class="chip" class:selected={filterStages.has(s)} onclick={() => toggleStage(s)}>{$stageLabel(s)}</button>
        {/each}
        {#if allStages.length === 0}<span class="text-xs text-dim">No stages.</span>{/if}
      </div>
    </div>

    <div class="strafe-filter-row">
      <div class="strafe-filter-label">User Engagement</div>
      <div class="chip-row">
        {#each ['never_contacted','engaged','stale'] as b}
          <button class="chip" class:selected={filterEngagement.has(b as EngagementBucket)} onclick={() => toggleEngagement(b as EngagementBucket)}>
            {ENGAGEMENT_LABELS[b as EngagementBucket]}
          </button>
        {/each}
      </div>
    </div>

    <div class="text-sm text-dim" style="margin-top:8px;">
      {filteredContacts.length} match · {selectedContacts.length} selected
    </div>
  </div>

  <!-- ── Contact list ── -->
  {#if filteredContacts.length > 0}
    <div class="card">
      <div class="card-header">
        <h4>Targets from CRM ({selectedContacts.length}/{filteredContacts.length})</h4>
        <span style="margin-left:auto; font-size:11px;">
          <button class="btn-tiny" onclick={checkAll}>Check all</button>
          <button class="btn-tiny" onclick={uncheckAll}>Uncheck all</button>
          <button class="btn-tiny" onclick={invertSelection}>Invert</button>
        </span>
      </div>
      <div class="strafe-target-list">
        {#each filteredContacts as c}
          <label class="strafe-target-row">
            <input type="checkbox"
              checked={!uncheckedEmails.has(c.email)}
              onchange={() => toggleContact(c.email)} />
            <span class="strafe-target-email">{c.email}</span>
            <span class="strafe-target-name">{c.name}</span>
            <span class="strafe-target-client text-dim">{c.client}</span>
            <span class="text-xs text-dim">{ENGAGEMENT_LABELS[classifyEngagement(c)]}</span>
          </label>
        {/each}
      </div>
    </div>
  {/if}

  <!-- ── Cold-list paste ── -->
  <div class="card">
    <div class="card-header">
      <h4>Cold list (paste fresh recipients not in CRM)</h4>
      <span class="text-dim" style="font-size:11px;">{coldListContacts.length} valid</span>
    </div>
    <textarea
      bind:value={coldListRaw}
      placeholder={'one per line\nemail@example.com\nor: email@example.com, Company Name, FirstName'}
      rows="6"
      class="recipients-input"
    ></textarea>
  </div>

  <!-- ── Email preview ── -->
  <div class="card">
    <div class="card-header">
      <h4>Email preview</h4>
      <button class="btn-tiny" onclick={() => showPreview = !showPreview}>{showPreview ? 'Hide' : 'Show'}</button>
      <span class="text-dim" style="font-size:11px;">First name: <strong>{previewFirstName}</strong></span>
    </div>
    {#if showPreview}
      <div class="email-preview">
        <div class="email-preview-block">
          <div class="email-preview-meta">
            <strong>Email 1</strong> · From: <code>Marcus Dealton &lt;{email1Sender}&gt;</code> · Day 0 · plain text
          </div>
          <pre class="email-preview-body">{previewEmail1}</pre>
        </div>

        <div class="email-preview-block">
          <div class="email-preview-meta">
            <strong>Email 2</strong> · From: <code>Sara &lt;sara@dealton.ai&gt;</code> · Day {delay2} · light HTML
            · Subject: <em>{selectedTemplate?.subject || ''}</em>
          </div>
          <pre class="email-preview-body">{previewEmail2}</pre>
        </div>

        <div class="email-preview-block">
          <div class="email-preview-meta">
            <strong>Email 3</strong> · From: <code>Daniel &lt;daniel@dealton.ai&gt;</code> · Day {delay3} · plain text
            · Subject: <em>Dealton Story: Claude Code for Sales</em>
          </div>
          <pre class="email-preview-body">{previewEmail3}</pre>
        </div>
      </div>
    {/if}
  </div>

  <!-- ── Email 1 sender picker ── -->
  <div class="card">
    <div class="card-header"><h4>Email 1 sender (Marcus envelope)</h4></div>
    <div class="text-xs text-dim" style="margin-bottom:8px;">
      Both options send AS the "Marcus Dealton" persona — only the envelope address differs.
      <code>marcus@</code> matches the display name and may land differently in inboxes.
    </div>
    <div class="chip-row">
      <label class="chip" class:selected={email1Sender === 'partner@dealton.ai'}>
        <input type="radio" bind:group={email1Sender} value="partner@dealton.ai" style="display:none;" />
        partner@dealton.ai
      </label>
      <label class="chip" class:selected={email1Sender === 'marcus@dealton.ai'}>
        <input type="radio" bind:group={email1Sender} value="marcus@dealton.ai" style="display:none;" />
        marcus@dealton.ai
      </label>
    </div>
  </div>

  <!-- ── Auto-cancel on signup ── -->
  <div class="card">
    <div class="card-header"><h4>Stop sending if recipient signs up</h4></div>
    <label style="display:flex; align-items:flex-start; gap:10px; font-size:13px; cursor:pointer;">
      <input type="checkbox" bind:checked={cancelOnSignup} style="margin-top:2px;" />
      <span>
        <strong>Auto-cancel remaining emails</strong> if the recipient creates a Dealton account mid-campaign.
        <span class="text-dim" style="display:block; font-size:12px; margin-top:2px;">
          Checked before each of the three sends. Already-sent emails are not affected.
          Recommended on — recipients who've already signed up shouldn't keep getting AI-persona cold mail.
        </span>
      </span>
    </label>
  </div>

  <!-- ── Schedule ── -->
  <div class="card">
    <div class="card-header"><h4>Schedule</h4></div>
    <div class="schedule-grid">
      <label><span>Initial offset (minutes)</span><input type="number" bind:value={initialOffset} min="0" /></label>
      <label><span>Email 2 delay (days)</span><input type="number" bind:value={delay2} min="0" /></label>
      <label><span>Email 3 delay (days)</span><input type="number" bind:value={delay3} min="0" /></label>
      <label>
        <span>Stagger between recipients (seconds)</span>
        <input type="number" bind:value={dispatchStagger} min="0" max="3600" />
      </label>
    </div>
    <p class="text-dim" style="font-size:11px; margin-top:8px;">
      Schedule defaults: <strong>0/3/7</strong> for production (email 1 today, email 2 in 3 days, email 3 in 7 days). Use <strong>0/0/0</strong> for smoke testing.
    </p>
    <p class="text-dim" style="font-size:11px; margin-top:4px;">
      <strong>Stagger</strong>: per-recipient gap on send. With <code>60s</code> across 50 recipients, email-1s fire over ~50 minutes instead of all in one scheduler tick — keeps Gmail's spam filters happy. <code>0</code> = burst (all due immediately, dispatched back-to-back).
    </p>
  </div>

  <!-- ── Launch ── -->
  <div class="launch-row">
    <button
      class="launch-btn"
      onclick={launch}
      disabled={!healthReady || launching || combinedRecipients.length === 0 || activeJob?.status === 'running' || activeJob?.status === 'queued'}
    >
      {launching ? 'Launching…' : `Launch Campaign (${combinedRecipients.length} recipients)`}
    </button>
  </div>

  <!-- ── Active job status ── -->
  {#if activeJob}
    <div class="card">
      <div class="card-header">
        <h4>Active job: {activeJob.id}</h4>
        <span class="badge badge-{activeJob.status}">{activeJob.status}</span>
        {#if activeJob.status === 'running' || activeJob.status === 'queued'}
          <button class="btn-small btn-danger" onclick={cancelActive}>Cancel</button>
        {/if}
      </div>
      <div class="job-stats">
        <span>{activeJob.completed}/{activeJob.total} completed</span>
        <span>{activeJob.failed} failed</span>
        <span>{activeJob.replied} replied</span>
        <span class="text-dim">template: {activeJob.template_id}</span>
      </div>
      <table class="targets-table">
        <thead><tr><th>Recipient</th><th>Status</th><th>M</th><th>S</th><th>D</th><th>Error</th></tr></thead>
        <tbody>
          {#each activeTargets as t}
            <tr>
              <td>{t.recipient_email}</td>
              <td><span class="badge badge-{t.status}">{t.status}</span></td>
              <td class="msg-id">{t.marcus_message_id ? '✓' : '—'}</td>
              <td class="msg-id">{t.sara_message_id ? '✓' : '—'}</td>
              <td class="msg-id">{t.daniel_message_id ? '✓' : '—'}</td>
              <td class="error-cell">{t.error_text || ''}</td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>
  {/if}

  <!-- ── Job history ── -->
  {#if jobHistory.length > 0}
    <div class="card">
      <div class="card-header"><h4>Past campaigns</h4></div>
      <table class="history-table">
        <thead><tr><th>Date</th><th>Job</th><th>Template</th><th>Status</th><th>Done/Total</th><th>Failed</th><th></th></tr></thead>
        <tbody>
          {#each jobHistory as j}
            <tr class:active={activeJobId === j.id}>
              <td>{formatDate(j.created_at)}</td>
              <td><code>{j.id.slice(0, 12)}</code></td>
              <td>{j.template_id}</td>
              <td><span class="badge badge-{j.status}">{j.status}</span></td>
              <td>{j.completed}/{j.total}</td>
              <td>{j.failed}</td>
              <td><button class="btn-tiny" onclick={() => viewJob(j.id)}>View</button></td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>
  {/if}

  <!-- ── Template editor modal ── -->
  {#if editingTemplate}
    <div class="modal-overlay" onclick={cancelEdit}>
      <div class="modal" onclick={(e) => e.stopPropagation()}>
        <h3>Edit template: {editingTemplate.id}</h3>
        <label><span>Label</span><input type="text" bind:value={editLabel} /></label>
        <label><span>Subject</span><input type="text" bind:value={editSubject} /></label>
        <label><span>Body</span><textarea bind:value={editBody} rows="20"></textarea></label>
        <div class="modal-actions">
          <button onclick={cancelEdit}>Cancel</button>
          <button class="btn-primary" onclick={saveEdit}>Save</button>
        </div>
      </div>
    </div>
  {/if}
</div>

<style>
  .meta-tab { padding: 12px 0; }
  .meta-header h3 { margin: 0 0 4px 0; font-size: 18px; }
  .meta-toast { background: var(--accent, #3b82f6); color: #fff; padding: 8px 14px; border-radius: 6px; margin-bottom: 12px; font-size: 13px; }
  .card { border: 1px solid var(--border); border-radius: 8px; padding: 16px; margin-bottom: 16px; background: var(--bg-surface); }
  .card-header { display: flex; align-items: center; gap: 12px; margin-bottom: 12px; }
  .card-header h4 { margin: 0; font-size: 14px; font-weight: 600; }
  .btn-small { font-size: 12px; padding: 4px 10px; }
  .btn-tiny { font-size: 11px; padding: 2px 8px; background: transparent; border: 1px solid var(--border); border-radius: 4px; cursor: pointer; color: var(--text); margin-left: 4px; }
  .btn-tiny:hover { background: var(--bg-hover, rgba(0,0,0,0.04)); }
  .btn-danger { color: #ef4444; border-color: #ef4444; }

  .sender-grid { display: flex; flex-direction: column; gap: 6px; }
  .sender-row { display: flex; align-items: center; gap: 10px; font-size: 13px; }
  .status-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
  .status-ok { background: #22c55e; }
  .status-missing { background: #9ca3af; }
  .status-expired { background: #f59e0b; }
  .status-failed { background: #ef4444; }
  .sender-email { font-weight: 500; min-width: 180px; }
  .sender-detail { font-size: 12px; }

  .template-list { display: flex; flex-direction: column; gap: 8px; }
  .template-card { border: 1px solid var(--border); border-radius: 6px; padding: 12px; display: flex; gap: 10px; cursor: pointer; transition: border-color 0.1s; align-items: flex-start; }
  .template-card:hover { border-color: var(--accent, #3b82f6); }
  .template-card.selected { border-color: var(--accent, #3b82f6); box-shadow: 0 0 0 1px var(--accent, #3b82f6); }
  /* Override the global `input { width: 100% }` from app.css for radio/checkbox */
  .meta-tab input[type="radio"],
  .meta-tab input[type="checkbox"] {
    width: 16px;
    height: 16px;
    padding: 0;
    margin: 0;
    flex-shrink: 0;
    background: transparent;
    border: none;
  }
  .template-card input[type="radio"] { margin-top: 3px; }
  .template-card-body { flex: 1; }
  .template-card-header { display: flex; align-items: center; gap: 8px; margin-bottom: 4px; }
  .template-label { font-weight: 600; font-size: 13px; flex: 1; }
  .template-subject { font-size: 12px; color: var(--text-dim); margin-bottom: 4px; }
  .template-preview { font-size: 11px; color: var(--text-dim); line-height: 1.4; }

  .strafe-filter-row { display: flex; gap: 12px; margin-bottom: 8px; align-items: flex-start; }
  .strafe-filter-label { font-size: 12px; font-weight: 600; min-width: 90px; padding-top: 4px; }
  /* .chip / .chip:hover / .chip.selected -> global (app.css), Today's values */

  .strafe-target-list { max-height: 320px; overflow-y: auto; border: 1px solid var(--border); border-radius: 4px; }
  .strafe-target-row { display: grid; grid-template-columns: 24px 1fr 1fr 1fr auto; gap: 8px; padding: 6px 12px; border-bottom: 1px solid var(--border); align-items: center; font-size: 12px; }
  .strafe-target-row:last-child { border-bottom: none; }
  .strafe-target-email { font-family: monospace; }
  .strafe-target-name { font-weight: 500; }

  .recipients-input { width: 100%; font-family: monospace; font-size: 12px; padding: 8px; border: 1px solid var(--border); border-radius: 4px; resize: vertical; }

  .email-preview { display: flex; flex-direction: column; gap: 16px; }
  .email-preview-block { border: 1px solid var(--border); border-radius: 6px; overflow: hidden; }
  .email-preview-meta { padding: 8px 12px; background: var(--bg-hover, rgba(0,0,0,0.03)); font-size: 11px; color: var(--text-dim); border-bottom: 1px solid var(--border); }
  .email-preview-meta code { font-size: 11px; }
  .email-preview-body { padding: 12px; margin: 0; font-family: var(--font-sans); font-size: 13px; line-height: 1.6; white-space: pre-wrap; word-wrap: break-word; max-height: 400px; overflow-y: auto; }

  .schedule-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
  .schedule-grid label { display: flex; flex-direction: column; gap: 4px; font-size: 12px; }
  .schedule-grid input { padding: 6px 8px; border: 1px solid var(--border); border-radius: 4px; font-size: 13px; }

  .launch-row { margin: 16px 0; text-align: center; }
  .launch-btn { background: var(--accent, #3b82f6); color: #fff; border: none; padding: 12px 32px; font-size: 14px; font-weight: 600; border-radius: 6px; cursor: pointer; }
  .launch-btn:disabled { background: var(--border); cursor: not-allowed; }

  .targets-table, .history-table { width: 100%; border-collapse: collapse; font-size: 12px; }
  .targets-table th, .targets-table td, .history-table th, .history-table td { text-align: left; padding: 6px 8px; border-bottom: 1px solid var(--border); }
  .msg-id { text-align: center; font-family: monospace; }
  .error-cell { color: #ef4444; font-size: 11px; }
  .job-stats { display: flex; gap: 16px; margin-bottom: 12px; font-size: 12px; }

  .badge { display: inline-block; font-size: 10px; padding: 2px 8px; border-radius: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; }
  .badge-ok, .badge-completed, .badge-sent { background: #d1fae5; color: #065f46; }
  .badge-running, .badge-queued, .badge-generating { background: #dbeafe; color: #1e40af; }
  .badge-pending { background: #f3f4f6; color: #4b5563; }
  .badge-failed, .badge-cancelled { background: #fee2e2; color: #991b1b; }
  .badge-replied { background: #fef3c7; color: #78350f; }

  .history-table tr.active { background: rgba(59, 130, 246, 0.06); }

  .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 1000; display: flex; align-items: center; justify-content: center; padding: 24px; }
  .modal { background: var(--bg-surface, #fff); border-radius: 8px; padding: 24px; max-width: 700px; width: 100%; max-height: 90vh; overflow-y: auto; }
  .modal h3 { margin: 0 0 16px 0; }
  .modal label { display: flex; flex-direction: column; gap: 4px; margin-bottom: 12px; font-size: 12px; }
  .modal input, .modal textarea { padding: 8px; border: 1px solid var(--border); border-radius: 4px; font-family: inherit; font-size: 13px; }
  .modal textarea { font-family: monospace; }
  .modal-actions { display: flex; gap: 8px; justify-content: flex-end; margin-top: 16px; }
  .btn-primary { background: var(--accent, #3b82f6); color: #fff; border: none; padding: 8px 16px; border-radius: 4px; font-weight: 600; cursor: pointer; }
</style>
